import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext'; // Import useAuth
// import { useLocation, useNavigate } from 'react-router-dom'; // No longer needed

const PatientContext = createContext();

export const usePatient = () => {
  return useContext(PatientContext);
};

export const PatientProvider = ({ children }) => {
  // Get user AND loading state from AuthContext
  const { user, loading: authLoading, subscriptionDetails } = useAuth();
  const [patients, setPatients] = useState(null); // Initialize patients state (null indicates loading)
  const [selectedPatient, setSelectedPatient] = useState(null);
  // Keep track of patient-specific loading state
  const [isPatientLoading, setIsPatientLoading] = useState(true);
  const [error, setError] = useState(null);
  // Removed location and navigate variables

  const fetchPatients = useCallback(async () => {
    console.log('PatientContext: fetchPatients called. Auth Loading:', authLoading, 'User:', user);

    // IMPORTANT: Check authLoading, user presence, and subscription status
    const isValidSub = subscriptionDetails?.subscription_status === 'active' || subscriptionDetails?.subscription_status === 'trialing';
    if (authLoading || !user || !isValidSub) {
      // ADD LOGGING HERE
      console.log('PatientContext: Skip fetch — authLoading:', authLoading,
                  'user:', !!user, 'validSub:', isValidSub);
      // Ensure patients are cleared if no user or auth is loading
      setIsPatientLoading(false); // Stop patient loading if we return early
      // Reset selected patient if user logs out
      if (!user) {
        setSelectedPatient(null);
      }
      return;
    }

    // Always set loading true before fetch when conditions are met
    setIsPatientLoading(true);
    setError(null);
    // ADD LOGGING HERE
    console.log('PatientContext: Proceeding to fetch for user:', user?.uid);
    let token = null;
    try {
      token = await user.getIdToken(); // Get the Firebase ID token
      // ADD LOGGING
      console.log(`PatientContext: Got token (exists=${!!token}) for user ${user.uid}`);
      
    } catch (e) {
      console.error("Error getting Firebase ID token:", e);
      setError("Authentication error. Please try refreshing.");
      setPatients([]);
      setIsPatientLoading(false); // Stop patient loading on token error
      return;
    }

    try {
      // TODO: Verify this API endpoint is correct
      // ADD LOGGING
      console.log(`PatientContext: Fetching patients with token (exists=${!!token})`);
      const response = await fetch(`/api/patients/${user.uid}`, {
        headers: {
          'Authorization': `Bearer ${token}`, // Add the Authorization header
        },
      });
      if (response.status === 401) {
        // Handle specific unauthorized error, maybe trigger logout or refresh token?
        // Consider calling logout() from AuthContext here
        throw new Error('Unauthorized: Please log in again.');
      }
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      // Ensure we are setting an array to the state
      if (Array.isArray(data)) {
          setPatients(data);
      } else if (data && Array.isArray(data.patients)) {
          // Common pattern: API returns { patients: [...] }
          setPatients(data.patients);
      } else {
          console.warn("API response for patients was not an array:", data);
          setPatients([]); // Default to empty array if response format is unexpected
      }
    } catch (e) {
      console.error("Failed to fetch patients:", e);
      setError(e.message || "Failed to load client data.");
      setPatients([]); // Set to empty array on error to stop loading state
    } finally {
      // Always set loading to false after fetch attempt
      setIsPatientLoading(false);
    }
  }, [user?.uid, authLoading, subscriptionDetails?.subscription_status]); // Corrected dependencies

  // Fetch patients on mount and when user or auth loading state changes
  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]); // fetchPatients dependency implicitly includes user and authLoading

  // Function to manually select a patient (used by Sidebar)
  const selectPatient = useCallback((patient) => {
      setSelectedPatient(patient);
  }, []);

  // Memoize the context value to prevent unnecessary re-renders of consumers
  const value = useMemo(() => ({
    patients,
    fetchPatients,
    selectedPatient,
    selectPatient,
    isLoading: isPatientLoading,
    error,
  }), [patients, fetchPatients, selectedPatient, selectPatient, isPatientLoading, error]);

  return (
    <PatientContext.Provider value={value}>
      {children}
    </PatientContext.Provider>
  );
};
